import { useTranslation } from "react-i18next";
import { useAuth } from "@/models/useAuth";
import { useUserSlice } from "@/models/useUserSlice";
import { Clipboard } from "@capacitor/clipboard";
import toast from "react-hot-toast";
import { CONTENT_LANGUAGES, type ContentLanguage } from "@/models/types";
import { contactUs, gotoPrivacy, gotoTerms } from "@/models/utils";
import { useEffect, useState } from "react";
import { App } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import { appLanguages, i18n } from "@/locales";
import { useRouter } from "@/_router";
import {
  CrownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  TranslateIcon,
  ShareIcon,
  LightbulbIcon,
  CloseIcon,
  LegalIcon,
  SparklesIcon,
} from "@/components/Icons";
import NativeContainer from "@/components/NativeContainer";
import { shareApp } from "@/models/useNative";

// Settings Button Component
const SettingsButton = ({
  text,
  subtext,
  onClick,
  destructive = false,
  icon,
}: {
  text: string;
  subtext?: string;
  onClick: () => void;
  destructive?: boolean;
  icon?: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`w-full group relative overflow-hidden p-5 rounded-3xl transition-all duration-300 active:scale-[0.98] text-left flex items-center justify-between shadow-sm ${
      destructive
        ? "bg-red-500/10 text-red-500 hover:bg-red-500/15"
        : "bg-[var(--glass-bg)] hover:bg-[var(--glass-bg-hover)] text-[var(--text-primary)]"
    }`}
  >
    <div className="flex items-center gap-5">
      {icon && (
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors shadow-inner ${
            destructive
              ? "bg-red-500/10 text-red-500"
              : "bg-[var(--text-primary)]/5 group-hover:bg-[var(--text-primary)]/10 text-[var(--text-primary)]"
          }`}
        >
          {icon}
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <span className="text-[17px] font-semibold tracking-tight">{text}</span>
        {subtext && (
          <span
            className={`text-[14px] font-medium ${
              destructive ? "text-red-400/80" : "text-[var(--text-secondary)]"
            }`}
          >
            {subtext}
          </span>
        )}
      </div>
    </div>
    {!destructive && (
      <div className="w-8 h-8 rounded-full flex items-center justify-center opacity-30 group-hover:opacity-100 transition-all transform group-hover:translate-x-1">
        <ChevronRightIcon size={20} className="text-[var(--text-secondary)]" />
      </div>
    )}
  </button>
);

export default function Settings({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation();
  const userData = useUserSlice((state) => state.userData);
  const isValidEmail = useUserSlice((state) => {
    const user = state?.userData;
    if (!user) return false;
    return !user.email.includes(user.id);
  });
  const router = useRouter();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isAiDisclosureOpen, setIsAiDisclosureOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll for header shadow effect
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 10);
  };

  const getCurrentLanguageLabel = () => {
    return (
      appLanguages.find((lang) => lang.value === i18n.language)?.label ||
      "English"
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!userData) {
      useUserSlice.getState().user().get();
    }
  }, []);

  // Android back button: close settings
  useEffect(() => {
    let backButtonListener: PluginListenerHandle;

    const setupListener = async () => {
      backButtonListener = await App.addListener("backButton", () => {
        if (onClose) {
          onClose();
        } else {
          router.replace(`/native/home`);
        }
      });
    };

    setupListener();

    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [onClose, router]);

  const _copy = async (text: string) => {
    Clipboard.write({ string: text }).then(() => {
      toast.success(t("settings.copied"), {
        style: {
          background: "rgba(17, 16, 34, 0.8)",
          color: "#fff",
          backdropFilter: "blur(10px)",
        },
      });
    });
  };

  const _selectLanguage = async (langValue: string) => {
    await useUserSlice.getState().user().update({ deviceLanguage: langValue });

    const contentCode = langValue === "tl" ? "fil" : langValue;
    const contentLangParam = CONTENT_LANGUAGES.some((l) => l.code === contentCode)
      ? `&contentLang=${contentCode}`
      : "";
    window.location.href = `${window.location.pathname}?appLang=${langValue}${contentLangParam}`;
  };

  return (
    <NativeContainer className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden selection:bg-[var(--accent)]/20">
      {/* Header */}
      <header
        className={`shrink-0 z-20 backdrop-blur-md transition-all duration-300 ${
          isScrolled ? "bg-[var(--bg-primary)]/80 shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="px-5 py-4 flex items-center justify-between">
          <button
            onClick={() =>
              onClose ? onClose() : router.replace(`/native/home`)
            }
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--glass-bg-hover)] backdrop-blur-md active:scale-90 transition-all text-[var(--text-primary)] hover:bg-[var(--glass-bg-active)]"
          >
            <ChevronLeftIcon size={26} />
          </button>
          <h1 className="text-[17px] font-medium tracking-wide text-[var(--text-primary)]/90">
            {t("settings.title")}
          </h1>
          <div className="w-12" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Content */}
      <main
        className="flex-1 px-5 py-6 space-y-8 relative z-10 overflow-y-auto pb-32"
        onScroll={handleScroll}
      >
        {/* Account Section */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.account")}
          </h2>
          <div className="space-y-3">
            {!isValidEmail ? (
              <div className="p-5 rounded-3xl bg-[var(--glass-bg)] flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {t("settings.signInToSync")}
                  </p>
                </div>
                <button
                  onClick={() =>
                    useUserSlice
                      .getState()
                      .user()
                      .nativeSignIn({
                        onSuccess: () => {
                          router.replace(`/native/home`);
                          window.location.reload();
                        },
                      })
                  }
                  className="shrink-0 px-5 py-2.5 rounded-full bg-[var(--accent)] text-[var(--text-inverse)] font-bold text-sm active:scale-95 transition-transform"
                >
                  {t("settings.signIn")}
                </button>
              </div>
            ) : (
              <SettingsButton
                text={userData?.email || t("settings.signIn")}
                subtext={t("settings.tapToCopyEmail")}
                onClick={() => userData?.email && _copy(userData.email)}
                icon={<CrownIcon size={24} />}
              />
            )}
          </div>
        </section>

        {/* Preferences Section */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.preferences")}
          </h2>
          <div className="space-y-3">
            {/* Language */}
            <div className="space-y-2">
              <SettingsButton
                text={t("settings.appLanguage", { language: "" })}
                subtext={getCurrentLanguageLabel()}
                onClick={() => setIsLangOpen(!isLangOpen)}
                icon={<TranslateIcon size={24} />}
              />
              {isLangOpen && (
                <div className="grid gap-2 pl-4 pr-1 py-2 animate-in slide-in-from-top-2 fade-in duration-200">
                  {appLanguages.map((lang) => (
                    <button
                      key={lang.value}
                      onClick={() => _selectLanguage(lang.value)}
                      className={`w-full p-4 rounded-2xl flex items-center justify-between transition-all ${
                        i18n.language === lang.value
                          ? "bg-[var(--glass-bg-active)] text-[var(--text-primary)] shadow-sm"
                          : "bg-[var(--glass-bg)] text-[var(--text-secondary)] hover:bg-[var(--glass-bg-active)]"
                      }`}
                    >
                      <span className="text-[15px] font-medium ml-1">
                        {lang.label}
                      </span>
                      {i18n.language === lang.value && (
                        <CheckIcon size={18} className="text-[var(--accent)]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.supportAndLegal")}
          </h2>
          <div className="space-y-3">
            <SettingsButton
              text={t("settings.contactSupport")}
              onClick={() => {
                contactUs(
                  encodeURIComponent(
                    `Hello I need help with purely novels (#DO NOT REMOVE: ${useUserSlice.getState()?.deviceId})`
                  )
                );
              }}
              icon={<LightbulbIcon size={24} />}
            />
            <SettingsButton
              text={t("settings.shareWithFriend")}
              onClick={shareApp}
              icon={<ShareIcon size={24} />}
            />
            <SettingsButton
              text={t("settings.aiGeneratedContent")}
              onClick={() => setIsAiDisclosureOpen(true)}
              icon={<SparklesIcon size={24} />}
            />
            <SettingsButton
              text={t("settings.privacyPolicy")}
              onClick={gotoPrivacy}
              icon={<LegalIcon size={24} />}
            />
            <SettingsButton
              text={t("settings.termsOfService")}
              onClick={gotoTerms}
              icon={<LegalIcon size={24} />}
            />
          </div>
        </section>

        {/* Danger Zone */}
        {isValidEmail && (
          <section className="space-y-4">
            <div className="pt-4">
              <SettingsButton
                text={t("settings.logout")}
                onClick={() => useAuth.signOut()}
                destructive
                icon={<CloseIcon size={24} />}
              />
            </div>
          </section>
        )}
      </main>

      {/* AI-Generated Content Disclosure Modal */}
      {isAiDisclosureOpen && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setIsAiDisclosureOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div
              className="w-full max-w-md bg-[var(--glass-bg)] backdrop-blur-2xl rounded-[32px] p-8 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[var(--text-primary)] text-xl font-bold flex items-center gap-2">
                  <SparklesIcon size={20} /> {t("settings.aiGeneratedContent")}
                </h2>
                <button
                  onClick={() => setIsAiDisclosureOpen(false)}
                  className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center active:scale-90 transition-transform"
                >
                  <CloseIcon size={20} />
                </button>
              </div>
              <p className="text-[var(--text-secondary)] text-[15px] leading-relaxed mb-4">
                {t("settings.aiDisclosureBody1")}
              </p>
              <p className="text-[var(--text-secondary)] text-[15px] leading-relaxed mb-6">
                {t("settings.aiDisclosureBody2")}
              </p>
            </div>
          </div>
        </div>
      )}
    </NativeContainer>
  );
}
